import {useState, useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {Avatar, Button, Table, Tag} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import moment from 'moment';
import {
  GET_SIMULATOR_RESTART_PURCHASES,
  GET_SIMULATOR_SUBSCRIPTIONS,
} from 'pages/simulatorConfig/resolvers';
import styles from '../analytics.module.scss';
import UserLink from 'components/userLink/UserLink';

const TYPE_TABS = [
  {key: 'PREMIUM', label: 'Premium'},
  {key: 'VIRTUALMONEY', label: 'Virtual Money'},
  {key: 'RESTART_PURCHASES', label: 'Restart Purchases'},
];

const PLAN_TYPE_FILTERS = [
  {text: 'Free', value: 'FREE'},
  {text: 'Premium', value: 'PREMIUM'},
];

const CYCLE_FILTERS = [
  {text: 'Monthly', value: 'MONTHLY'},
  {text: 'Yearly', value: 'YEARLY'},
];

const CHANNEL_FILTERS = [
  {text: 'Wallet', value: 'WALLET'},
  {text: 'Other', value: 'OTHER'},
];

const ACTIVE_FILTERS = [
  {text: 'Active', value: 'true'},
  {text: 'Inactive', value: 'false'},
];

const getFirstFilterValue = values => values?.[0];

export const SimulatorSubscriptionsTable = () => {
  const [activeTab, setActiveTab] = useState('PREMIUM');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({});
  const [sortOrder, setSortOrder] = useState();
  const isRestartPurchasesTab = activeTab === 'RESTART_PURCHASES';
  const isPremiumTab = activeTab === 'PREMIUM';

  const queryInput = useMemo(() => {
    const active = filters.active;
    const subscriptionType = isPremiumTab ? 'SUBSCRIPTION' : activeTab;

    return {
      page: currentPage,
      limit,
      subscriptionType,
      ...(isPremiumTab
        ? {planType: 'PREMIUM'}
        : filters.planType && {planType: filters.planType}),
      ...(filters.cycle && {cycle: filters.cycle}),
      ...(filters.channel && {channel: filters.channel}),
      ...(active !== undefined && {active}),
      ...(sortOrder && {sortOrder}),
    };
  }, [activeTab, currentPage, filters, isPremiumTab, limit, sortOrder]);

  const restartPurchasesInput = useMemo(
    () => ({
      page: currentPage,
      limit,
    }),
    [currentPage, limit],
  );

  const {data: subscriptionsData, loading: subscriptionsLoading} = useQuery(
    GET_SIMULATOR_SUBSCRIPTIONS,
    {
      variables: {input: queryInput},
      fetchPolicy: 'network-only',
      skip: isRestartPurchasesTab,
      onError: error => toast.error(error.message),
    },
  );

  const {data: restartPurchasesData, loading: restartPurchasesLoading} =
    useQuery(GET_SIMULATOR_RESTART_PURCHASES, {
      variables: {input: restartPurchasesInput},
      fetchPolicy: 'network-only',
      skip: !isRestartPurchasesTab,
      onError: error => toast.error(error.message),
    });

  const data = isRestartPurchasesTab ? restartPurchasesData : subscriptionsData;
  const loading = isRestartPurchasesTab
    ? restartPurchasesLoading
    : subscriptionsLoading;
  const hasTableState =
    !isRestartPurchasesTab &&
    (Object.values(filters).some(value => value !== undefined) ||
      sortOrder ||
      currentPage !== 1 ||
      limit !== 10);

  const handleTypeChange = key => {
    setActiveTab(key);
    setCurrentPage(1);
    setFilters({});
    setSortOrder();
  };

  const handleResetTable = () => {
    setCurrentPage(1);
    setLimit(10);
    setFilters({});
    setSortOrder();
  };

  const handleTableChange = (pagination, tableFilters = {}, sorter = {}) => {
    setCurrentPage(pagination.current);
    setLimit(pagination.pageSize);

    if (isRestartPurchasesTab) {
      return;
    }

    const activeFilter = getFirstFilterValue(tableFilters.active);

    setFilters({
      ...(!isPremiumTab && {
        planType: getFirstFilterValue(tableFilters.planType),
      }),
      cycle: getFirstFilterValue(tableFilters.cycle),
      channel: getFirstFilterValue(tableFilters.channel),
      ...(activeFilter !== undefined && {active: activeFilter === 'true'}),
    });
    setSortOrder(
      sorter?.order
        ? {ascend: 'asc', descend: 'desc'}[sorter.order]
        : undefined,
    );
  };

  const subscriptionColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const user = record?.user;
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
            <Avatar src={user?.imageUrl} icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span style={{fontWeight: 500, color: '#111827'}}>
                <UserLink userId={user?.id}>
                  {user?.firstName} {user?.lastName}
                </UserLink>
              </span>
              <span style={{fontSize: '12px', color: '#6b7280'}}>
                {user?.email}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Plan Type',
      dataIndex: 'planType',
      key: 'planType',
      filters: isPremiumTab ? undefined : PLAN_TYPE_FILTERS,
      filterMultiple: false,
      filteredValue: isPremiumTab
        ? ['PREMIUM']
        : filters.planType
        ? [filters.planType]
        : null,
      render: text => (
        <Tag color={text === 'PREMIUM' ? 'gold' : 'blue'}>{text}</Tag>
      ),
    },
    {
      title: 'Subscription Type',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: text => <Tag>{text}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) => `${record.currency} ${text}`,
    },

    {
      title: 'Cycle',
      dataIndex: 'cycle',
      key: 'cycle',
      filters: CYCLE_FILTERS,
      filterMultiple: false,
      filteredValue: filters.cycle ? [filters.cycle] : null,
      render: text => <Tag>{text}</Tag>,
    },
    {
      title: 'Reset count',
      dataIndex: 'resetCount',
      key: 'resetCount',
      render: value => (value != null && value !== '' ? value : '—'),
    },
    {
      title: 'Last reset',
      dataIndex: 'lastResetAt',
      key: 'lastResetAt',
      render: text => (text ? moment(text).format('M/D/YY hh:mm a') : 'N/A'),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      filters: ACTIVE_FILTERS,
      filterMultiple: false,
      filteredValue:
        filters.active !== undefined ? [String(filters.active)] : null,
      render: active => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      filters: CHANNEL_FILTERS,
      filterMultiple: false,
      filteredValue: filters.channel ? [filters.channel] : null,
      render: text => <Tag>{text}</Tag>,
    },
    {
      title: 'Started At',
      dataIndex: 'startedAt',
      key: 'startedAt',
      sorter: true,
      sortOrder:
        sortOrder === 'asc'
          ? 'ascend'
          : sortOrder === 'desc'
          ? 'descend'
          : null,
      render: text => moment(text).format('M/D/YY hh:mm a'),
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: text => (text ? moment(text).format('M/D/YY hh:mm a') : 'N/A'),
    },
  ];

  const restartPurchaseColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const user = record?.user;
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
            <Avatar icon={<UserOutlined />} />
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <span style={{fontWeight: 500, color: '#111827'}}>
                <UserLink userId={user?.id}>
                  {user?.firstName} {user?.lastName}
                </UserLink>
              </span>
              <span style={{fontSize: '12px', color: '#6b7280'}}>
                {user?.email}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text, record) => `${record.currency} ${text}`,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => <Tag>{status}</Tag>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: text => (text ? moment(text).format('M/D/YY hh:mm a') : 'N/A'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: text => (text ? moment(text).format('M/D/YY hh:mm a') : 'N/A'),
    },
  ];

  const dataSource = isRestartPurchasesTab
    ? data?.getSimulatorRestartPurchases?.data || []
    : data?.getSimulatorSubscriptions?.data || [];
  const total = isRestartPurchasesTab
    ? data?.getSimulatorRestartPurchases?.pagination?.total || 0
    : data?.getSimulatorSubscriptions?.pagination?.total || 0;
  const columns = isRestartPurchasesTab
    ? restartPurchaseColumns
    : subscriptionColumns;

  return (
    <div className={styles.membersTableContainer}>
      <div className={styles.membersHeader}>
        <h3 className={styles.sectionTitle}>Simulator transactions</h3>
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
          {!isRestartPurchasesTab && (
            <Button disabled={!hasTableState} onClick={handleResetTable}>
              Reset table
            </Button>
          )}
          <div className={styles.membersTabs}>
            {TYPE_TABS.map(tab => (
              <div
                key={tab.key}
                className={`${styles.tabItem} ${
                  activeTab === tab.key ? styles.activeTab : ''
                }`}
                onClick={() => handleTypeChange(tab.key)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleTypeChange(tab.key);
                  }
                }}>
                {tab.label}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: limit,
          total,
          showSizeChanger: true,
          position: ['bottomRight'],
        }}
        onChange={handleTableChange}
        className={styles.antdTable}
      />
    </div>
  );
};
